import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAstrologerProfile } from '../../hooks/useAstrologerProfile';
import { PhoneInput } from '../ui/PhoneInput';
import { BirthDateInput } from '../ui/BirthDateInput';
import { BirthTimeInput } from '../ui/BirthTimeInput';
import { CityAutocomplete } from '../ui/CityAutocomplete';

interface GeoResult { city: string; lat: number; lng: number; timezone: string }

async function geocodeCity(query: string): Promise<GeoResult | null> {
  try {
    const res = await fetch(
      `/api/nominatim-proxy?q=${encodeURIComponent(query)}&format=json&limit=1&addressdetails=1`,
    );
    const data = await res.json();
    if (!data?.length) return null;
    const r = data[0];
    const city = r.display_name.split(',')[0].trim();
    return { city, lat: parseFloat(r.lat), lng: parseFloat(r.lon), timezone: 'America/Sao_Paulo' };
  } catch { return null; }
}

function buildNatalHash(date: string, time: string, lat: number, lng: number): string {
  return `${date}|${time ?? ''}|${lat.toFixed(4)}|${lng.toFixed(4)}`;
}

export function ProfessionalClientNew() {
  const navigate = useNavigate();
  const { astrologerProfile } = useAstrologerProfile();

  const [name,          setName]          = useState('');
  const [email,         setEmail]         = useState('');
  const [phone,         setPhone]         = useState('');
  const [whatsapp,      setWhatsapp]      = useState('');
  const [gender,        setGender]        = useState('');
  const [tags,          setTags]          = useState('');
  const [birthDate,     setBirthDate]     = useState('');
  const [birthTime,     setBirthTime]     = useState('');
  const [timeUnknown,   setTimeUnknown]   = useState(false);
  const [birthCity,     setBirthCity]     = useState('');
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState<string | null>(null);
  const [chartResult,   setChartResult]   = useState<{ sun: string; moon: string; mercury: string } | null>(null);
  const [savedId,       setSavedId]       = useState<string | null>(null);

  async function handleSave() {
    if (!astrologerProfile) return;
    if (!name.trim() || !birthDate) { setError('Nome e data de nascimento são obrigatórios.'); return; }

    setSaving(true);
    setError(null);

    try {
      // Geocode city
      let lat = 0, lng = 0, tz = 'America/Sao_Paulo', resolvedCity = birthCity;
      if (birthCity.trim()) {
        const geo = await geocodeCity(birthCity);
        if (geo) { lat = geo.lat; lng = geo.lng; tz = geo.timezone; resolvedCity = geo.city; }
      }

      const hash = buildNatalHash(birthDate, timeUnknown ? '' : birthTime, lat, lng);

      // Calculate natal chart via edge function
      let natalJson = null;
      try {
        const { data: chartData } = await supabase.functions.invoke('birth-chart', {
          body: {
            birth_date: birthDate,
            birth_time: timeUnknown ? null : birthTime || null,
            birth_city: resolvedCity,
            birth_lat:  lat || null,
            birth_lng:  lng || null,
            for_client: true,
          },
        });
        if (chartData?.ok && chartData.data?.planets) {
          const planets = chartData.data.planets as { name: string; sign: string }[];
          natalJson = {
            sun_sign:      planets.find(p => p.name === 'Sol')?.sign ?? '',
            moon_sign:     planets.find(p => p.name === 'Lua')?.sign ?? '',
            mercury_sign:  planets.find(p => p.name === 'Mercúrio')?.sign ?? '',
            venus_sign:    planets.find(p => p.name === 'Vênus')?.sign ?? '',
            mars_sign:     planets.find(p => p.name === 'Marte')?.sign ?? '',
            ascendant:     chartData.data.houses?.[0]?.sign ?? null,
            planets,
          };
        }
      } catch { /* chart optional */ }

      const tagList = tags.split(',').map(t => t.trim()).filter(Boolean);

      const { data, error: dbError } = await supabase
        .from('astrologer_clients')
        .insert({
          astrologer_id:              astrologerProfile.id,
          name:                       name.trim(),
          email:                      email.trim() || null,
          phone:                      phone.trim() || null,
          whatsapp:                   whatsapp.trim() || null,
          gender:                     gender || null,
          tags:                       tagList.length ? tagList : null,
          birth_date:                 birthDate,
          birth_time:                 (timeUnknown || !birthTime) ? null : birthTime,
          birth_time_unknown:         timeUnknown,
          birth_city:                 resolvedCity || null,
          birth_lat:                  lat || null,
          birth_lng:                  lng || null,
          birth_timezone:             tz,
          natal_chart_json:           natalJson,
          natal_chart_calculated_at:  natalJson ? new Date().toISOString() : null,
          natal_chart_hash:           hash,
        })
        .select('id')
        .single();

      if (dbError) {
        if (dbError.code === '23505') {
          throw new Error('Já existe um cliente ativo com esse e-mail nesta conta. Use um e-mail diferente ou deixe o campo em branco.');
        }
        throw new Error(dbError.message);
      }

      if (natalJson) {
        setChartResult({ sun: natalJson.sun_sign, moon: natalJson.moon_sign, mercury: natalJson.mercury_sign });
      }
      setSavedId(data!.id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (chartResult && savedId) {
    return (
      <div style={{ maxWidth: '560px' }}>
        <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
          <p style={{ fontSize: '2rem', margin: '0 0 0.75rem' }}>✓</p>
          <p style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 'var(--text-label)', margin: '0 0 0.5rem' }}>
            Mapa de {name} calculado com sucesso ✦
          </p>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: '0 0 1.5rem' }}>
            Sol em {chartResult.sun} · Lua em {chartResult.moon} · Mercúrio em {chartResult.mercury}
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
            <button className="btn-primary" onClick={() => navigate(`/profissional/clientes/${savedId}?tab=analises`)}>
              Gerar primeira análise
            </button>
            <button className="btn-secondary" onClick={() => navigate(`/profissional/clientes/${savedId}`)}>
              Ver perfil do cliente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '640px' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <button className="btn-secondary" style={{ fontSize: 'var(--text-caption)', marginBottom: '0.75rem' }}
          onClick={() => navigate('/profissional/clientes')}>
          ← Voltar
        </button>
        <h1 style={{ fontSize: 'clamp(1.25rem,3vw,1.5rem)', color: 'var(--text-primary)', margin: 0, fontFamily: 'var(--font-display)' }}>
          Novo Cliente
        </h1>
      </div>

      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem', fontSize: 'var(--text-label)' }}>
          Dados pessoais
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
          <div>
            <label className="form-label">Nome completo *</label>
            <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="Ana Souza" />
          </div>
          <div>
            <label className="form-label">E-mail</label>
            <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="ana@email.com" />
          </div>
          <div>
            <label className="form-label">Telefone</label>
            <PhoneInput value={phone} onChange={setPhone} />
          </div>
          <div>
            <label className="form-label">WhatsApp</label>
            <PhoneInput value={whatsapp} onChange={setWhatsapp} />
          </div>
          <div>
            <label className="form-label">Gênero (linguagem das análises)</label>
            <select className="form-input" value={gender} onChange={e => setGender(e.target.value)}>
              <option value="">Não especificar</option>
              <option value="F">Feminino</option>
              <option value="M">Masculino</option>
              <option value="NB">Não-binário</option>
            </select>
          </div>
          <div>
            <label className="form-label">Tags (separadas por vírgula)</label>
            <input className="form-input" value={tags} onChange={e => setTags(e.target.value)} placeholder="VIP, grupo terça" />
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem', fontSize: 'var(--text-label)' }}>
          Dados de nascimento
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="form-label">Data de nascimento *</label>
            <BirthDateInput value={birthDate} onChange={setBirthDate} required />
          </div>
          <div>
            <label className="form-label">Hora de nascimento</label>
            <BirthTimeInput value={birthTime} onChange={setBirthTime} disabled={timeUnknown} />
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.35rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', cursor: 'pointer' }}>
              <input type="checkbox" checked={timeUnknown} onChange={e => { setTimeUnknown(e.target.checked); if (e.target.checked) setBirthTime(''); }} style={{ accentColor: 'var(--petrol)' }} />
              Hora desconhecida
            </label>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="form-label">Local de nascimento</label>
            <CityAutocomplete value={birthCity} onChange={setBirthCity} placeholder="São Paulo, Brasil" />
          </div>
        </div>

        {timeUnknown && (
          <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.85rem', background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.3)', borderRadius: '6px' }}>
            <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: '#fbbf24' }}>
              ⚠ Algumas análises (como Ascendente e Casas) serão menos precisas sem o horário de nascimento.
            </p>
          </div>
        )}
      </div>

      {error && <p style={{ color: '#f87171', fontSize: 'var(--text-caption)', marginBottom: '0.75rem' }}>{error}</p>}

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button className="btn-primary" onClick={handleSave} disabled={saving} style={{ flex: 1 }}>
          {saving ? 'Salvando e calculando mapa...' : 'Salvar cliente'}
        </button>
        <button className="btn-secondary" onClick={() => navigate('/profissional/clientes')} disabled={saving}>
          Cancelar
        </button>
      </div>
    </div>
  );
}
